// 「最近刪除的歌」：編輯頁歌單下面的入口（沒有刪掉的歌就不顯示）+ 視窗。
// 刪掉的歌留 30 天，這段時間製作可以救回來（cue、留言、影片、音樂都還在，歌單順序回到原本的位置）；30 天後排程才真的清掉。
// 跟影片版本的「最近刪除」（DeletedVersions.tsx）同一套樣子。後台：cf-worker/songTrash.mjs（只有製作看得到、救得回來）。
import { useCallback, useEffect, useRef, useState } from 'react';
import { History, LoaderCircle, RotateCcw, X } from 'lucide-react';
import Icon from './Icon';
import {
  daysLeftText, deletedSongInside, deletedSongMeta, deletedSongTitle, isSoon, listDeletedSongs, restoredSongToast,
  restoreDeletedSong, restoreErrorText, showTrashEntry, SONG_KEEP_DAYS, trashEntryLabel, type DeletedSong,
} from '../lib/songTrashApi';
import { toast } from '../lib/toast';
import { useEscapeClose } from '../lib/useEscapeClose';
import './SongVideosDialog.css';
import './DeletedVersions.css';
import './DeletedSongs.css';

interface EntryProps {
  projectId: string;
  /** 只有製作看得到（editorRole === 'admin'）；別人不讀、不顯示 */
  enabled: boolean;
  /** 變了就重新讀（例如剛刪了一首歌） */
  reloadKey?: unknown;
  /** 救回來了 → 歌單重新讀 */
  onRestored: (song: DeletedSong) => void;
}

export default function DeletedSongsEntry({ projectId, enabled, reloadKey, onRestored }: EntryProps) {
  const [items, setItems] = useState<DeletedSong[]>([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) { setItems([]); return; }
    try {
      const r = await listDeletedSongs(projectId);
      setItems(r.songs);
    } catch {
      // 讀不到（網路不穩、舊版後台沒有這個功能）→ 入口先不顯示，下次刪歌或重新整理再讀
      setItems([]);
    }
  }, [projectId, enabled]);
  useEffect(() => { void load(); }, [load, reloadKey]);
  // 最後一首也救回來了 → 關掉視窗
  useEffect(() => { if (open && items.length === 0) setOpen(false); }, [open, items.length]);

  if (!showTrashEntry(enabled, items.length)) return null;
  return (
    <>
      <button type="button" className="btn btn--ghost editor-songs__trash" onClick={() => { setOpen(true); void load(); }}
        title={`刪掉的歌留 ${SONG_KEEP_DAYS} 天，這段時間可以救回來`}>
        <Icon icon={History} size="sm" /> {trashEntryLabel(items.length)}
      </button>
      <DeletedSongsDialog
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        projectId={projectId}
        reload={load}
        onRestored={onRestored}
      />
    </>
  );
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  items: DeletedSong[];
  projectId: string;
  /** 重新讀清單（救回來之後） */
  reload: () => Promise<void>;
  onRestored: (song: DeletedSong) => void;
}

export function DeletedSongsDialog({ open, onClose, items, projectId, reload, onRestored }: DialogProps) {
  useEscapeClose(open, onClose);
  const panelRef = useRef<HTMLDivElement>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  useEffect(() => { if (open) panelRef.current?.focus(); }, [open]);

  async function restore(s: DeletedSong) {
    setBusyId(s.id);
    try {
      await restoreDeletedSong(projectId, s.id);
      toast.success(restoredSongToast(s));
      await reload();
      onRestored(s);
    } catch (e) {
      toast.error(restoreErrorText(e));
      await reload();   // 剛好被別人救回來 / 超過期限了 → 清單跟著更新
    } finally {
      setBusyId(null);
    }
  }

  if (!open) return null;
  return (
    <div className="songvid-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panelRef} className="songvid delvid-dialog delsong-dialog" role="dialog" aria-modal="true" aria-labelledby="delsong-title" tabIndex={-1}
        onKeyDown={(e) => e.stopPropagation()}>
        <header className="songvid__head">
          <div className="songvid__titles">
            <h2 id="delsong-title"><Icon icon={History} /> 最近刪除的歌</h2>
            <p className="songvid__sub">刪掉的歌留 {SONG_KEEP_DAYS} 天，這段時間可以救回來：cue、留言、影片版本、音樂都原封不動，歌單順序也回到原本的位置；{SONG_KEEP_DAYS} 天後就真的清掉，救不回來了。</p>
          </div>
          <button type="button" className="songvid__close" onClick={onClose} title="關閉" aria-label="關閉">
            <Icon icon={X} size="md" />
          </button>
        </header>
        <div className="songvid__body">
          <ul className="delvid__list" aria-label="最近刪除的歌">
            {items.map(s => {
              const inside = deletedSongInside(s);
              return (
                <li key={s.id} className="delvid-row delsong-row">
                  <div className="delvid-row__main">
                    <div className="delvid-row__title" title={deletedSongTitle(s)}>
                      {s.code && <span className="delsong-row__code">{s.code}</span>}
                      {s.name}
                    </div>
                    <div className="delvid-row__meta">
                      {deletedSongMeta(s)}
                      {' · '}
                      <span className={isSoon(s.daysLeft) ? 'delvid-row__left is-soon' : 'delvid-row__left'}>{daysLeftText(s.daysLeft)}</span>
                    </div>
                    {inside && <div className="delvid-row__meta">裡面有 {inside}</div>}
                  </div>
                  <button type="button" className="songvid-btn" onClick={() => void restore(s)} disabled={busyId !== null}
                    aria-label={`救回來：${deletedSongTitle(s)}`}>
                    {busyId === s.id ? <><Icon icon={LoaderCircle} spin size="sm" /> 救回中…</> : <><Icon icon={RotateCcw} size="sm" /> 救回來</>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <footer className="songvid__foot">
          <span className="songvid__count">{items.length} 首</span>
          <button type="button" className="btn btn--ghost" onClick={onClose}>關閉</button>
        </footer>
      </div>
    </div>
  );
}
